import { formatDate as formatDatePipe } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  inject,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
  ViewEncapsulation,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { ActivatedRoute, Router } from '@angular/router';
import { debounceTime, merge, Subject, takeUntil } from 'rxjs';

import { OrderService } from 'src/app/core/services/order';
import { StateService } from 'src/app/core/services/state';
import * as Orders from '../../../domain';
import OrderRow from '../order-table/order-row.interface';
import { OrderTableComponent } from '../order-table/order-table.component';
import { CheckboxSelectComponent } from './components/checkbox-select/checkbox-select.component';
import { CheckboxFilterService } from './services/checkbox-filter.service';

@Component({
  selector: 'app-table-page',
  templateUrl: './table-page.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  providers: [CheckboxFilterService],
  standalone: true,
  imports: [
    FormsModule,
    MatIconModule,
    CheckboxSelectComponent,
    OrderTableComponent,
  ],
})
export class TablePageComponent
  implements OnChanges, OnInit, AfterViewInit, OnDestroy
{
  @Input() orders: Orders.Order[];

  @Input() noOrders: boolean = false;

  @Output() selectOrder: EventEmitter<Orders.Order> = new EventEmitter();

  @Output() csvDataChange = new EventEmitter<Orders.OrderCsvDataRow[]>();

  @ViewChild('orderTable') orderTable: OrderTableComponent;

  @ViewChild('serviceFilter') serviceFilter?: CheckboxSelectComponent<string>;

  @ViewChild('statusFilter')
  statusFilter?: CheckboxSelectComponent<Orders.Status>;

  @ViewChild('addressFilter') addressFilter?: CheckboxSelectComponent<string>;

  public searchQuery = '';

  closeOtherFilters(except: 'service' | 'status' | 'address'): void {
    if (except !== 'service') this.serviceFilter?.closeDropdown();
    if (except !== 'status') this.statusFilter?.closeDropdown();
    if (except !== 'address') this.addressFilter?.closeDropdown();
  }

  public lastUpdate: string = '...';

  public loading: boolean = true;

  public data: OrderRow[];

  public showFilterBt: boolean = false;

  public readonly checkboxFilters = Object.freeze({
    status: new CheckboxFilterService<Orders.Status>(),
    service: new CheckboxFilterService<string>(),
    address: new CheckboxFilterService<string>(),
  });

  private readonly destroy$ = new Subject<void>();

  private readonly changeDetectorRef$ = inject(ChangeDetectorRef);

  private readonly activatedRoute$ = inject(ActivatedRoute);

  private readonly orderService$ = inject(OrderService);

  private readonly stateService$ = inject(StateService);

  private readonly router$ = inject(Router);

  ngOnInit(): void {
    this.orderService$.getLastUpdated().then((x) => {
      this.lastUpdate = x;
    });
    document.querySelector('body').classList.add('orders-table-page');
    this.resetFilters();

    merge(
      this.checkboxFilters.status.onCheck$,
      this.checkboxFilters.service.onCheck$,
      this.checkboxFilters.address.onCheck$,
    )
      .pipe(takeUntil(this.destroy$), debounceTime(1))
      .subscribe(() => {
        this.updateData();
        this.router$.navigate([], {
          relativeTo: this.activatedRoute$,
          queryParamsHandling: 'merge',
          replaceUrl: true,
          queryParams: {
            status: this.checkboxFilters.status.selectedValues,
            service: this.checkboxFilters.service.selectedValues,
            address: this.checkboxFilters.address.selectedValues,
          },
        });
        this.changeDetectorRef$.detectChanges();
      });
  }

  ngOnChanges(changes) {
    const orderChanges = changes.orders;
    if (orderChanges.currentValue !== undefined) {
      this.resetFilters();
      this.updateData();
    }
  }

  ngAfterViewInit() {
    window.setTimeout(() => {
      this.stateService$.sendState('hide_loading_overlay');
    }, 500);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    document.querySelector('body').classList.remove('orders-table-page');
  }

  public removeFromFilter(
    type: 'service' | 'status' | 'address',
    value: any,
  ): void {
    for (const node of this.checkboxFilters[type].children) {
      if (node.attrs.value !== value) continue;
      node.value = false;
      break;
    }

    this.updateData();
  }

  public selectStatusFilter(st: any[], override?: boolean): void {
    const filterStatus = this.checkboxFilters.status;

    const includingStatus = filterStatus.children.filter((s) =>
      st.includes(s.attrs.value),
    );
    if (override) filterStatus.selectedChildren = includingStatus;
    else
      filterStatus.selectedChildren =
        filterStatus.selectedChildren.concat(includingStatus);
  }

  public formatDate(date: any): string {
    if (date == null || date == undefined) return '-';
    return formatDatePipe(date, 'MM/dd/yyyy', 'en-US');
  }

  private resetFilters(): void {
    this.showFilterBt = window.innerWidth <= 700;

    this.checkboxFilters.status.reset(
      { label: 'Show All Statuses', value: null! },
      [
        {
          label: 'Complete',
          value: Orders.Status.COMPLETE,
        },
        {
          label: 'In Progress',
          value: Orders.Status.IN_PROGRESS,
        },
        {
          label: 'Pending',
          value: Orders.Status.PENDING,
        },
        {
          label: 'Canceled',
          value: Orders.Status.CANCELED,
        },
      ],
    );

    const services = [
      ...new Set(this.orders.map((i) => i.service_name)),
    ].sort();
    if (services.length <= 1) services.pop();
    this.checkboxFilters.service.reset(
      { label: 'Show All Services', value: null! },
      services.map((type) => ({ label: type, value: type })),
    );

    const addresses = [...new Set(this.orders.map((i) => i.address))].sort();
    if (addresses.length <= 1) addresses.pop();
    this.checkboxFilters.address.reset(
      { label: 'Show All Addresses', value: null! },
      addresses.map((type) => ({ label: type, value: type })),
    );

    // #region MAP URL PARAMS
    const { status, service, address } = this.checkboxFilters;
    const { queryParamMap } = this.activatedRoute$.snapshot;

    const statusValues = this.normalizeStatusFilterValues(
      queryParamMap.getAll('status').map((s) => +s),
    );

    const addressParams = queryParamMap.getAll('address');
    address.selectedValues = addressParams.length
      ? addressParams
      : queryParamMap.getAll('location');
    service.selectedValues = queryParamMap.getAll('service');
    status.selectedValues = statusValues;
    // #endregion
  }

  private updateData(): void {
    const filteredOrders = this.filterData(this.orders, {
      address: this.checkboxFilters.address.selectedValues,
      service: this.checkboxFilters.service.selectedValues,
      status: this.checkboxFilters.status.selectedValues,
    });

    this.data = filteredOrders.reduce(
      (acc: OrderRow[], order: Orders.Order) => {
        if (order?.id) {
          acc.push({
            id: order?.id,
            deal: order.deal,
            oli: order.oli,
            line_type: order.line_type,
            nit: order.nit,
            site: order.site,
            address: order.address,
            city: order.city,
            state: order.state,
            service: order.service_name,
            status: order?.getStatus(true),
            foc_date: order.formatDate(order.foc_date),
            expanded: false,
            full_order: order,
          });
        }
        return acc;
      },
      [],
    );
    this.csvDataChange.emit(Orders.getOrderCSVData(this.data));
    this.changeDetectorRef$.detectChanges();
    this.loading = false;
  }

  private filterData(
    orders: Orders.Order[],
    filters: {
      service?: string[];
      status?: Orders.Status[];
      address?: string[];
    } = {},
  ): Orders.Order[] {
    const {
      address = [],
      service = [],
      status = [],
    } = structuredClone(filters);

    return orders.filter((order) => {
      if (service.length && !service.includes(order.service_name)) {
        return false;
      }
      if (address.length && !address.includes(order.address)) {
        return false;
      }
      if (status.length && !status.includes(order.milestone)) {
        return false;
      }
      return true;
    });
  }

  private normalizeStatusFilterValues(
    values: Orders.Status[],
  ): Orders.Status[] {
    const validStatuses = new Set(
      this.checkboxFilters.status.children.map((s) => s.attrs.value),
    );
    const response = new Set<Orders.Status>();

    for (const value of values) {
      if (validStatuses.has(value)) {
        response.add(value);
        continue;
      }

      if (this.isPendingStatus(value)) {
        response.add(Orders.Status.FOC_PENDING);
      }
    }

    return Array.from(response);
  }

  private isPendingStatus(status: Orders.Status): boolean {
    return [
      Orders.Status.FOC_PENDING_WAITING_VENDOR,
      Orders.Status.FOC_PENDING_SPECIAL_CONSTRUCTION,
      Orders.Status.FOC_PENDING_CARRIER_BUILD,
      Orders.Status.FOC_PENDING_CUST_REQ,
      Orders.Status.FOC_PENDING,
    ].includes(status);
  }
}
